import { Group, MultiSelect, Select } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import standardEventTypes from '../constants/eventTypes';
import useEventTypes from '../helpers/useEventTypes';
import ErrorMessage from './ErrorMessage';
import EventIcon from './Profiles/EventIcon';
import Spinner from './Spinner';

const EventSelector = ({
  actions,
  setActions,
  isMulti,
  allowedEventsForTrigger = false,
  excludeHighFrequency = false,
  accountId,
  domainId,
}) => {
  const { domain } = useSelector((state) => state.domain);
  const [loadError, setLoadError] = useState();

  // Use explicit props if provided, otherwise fall back to Redux domain
  const effectiveAccountId = accountId ?? domain?.accountId;
  const effectiveDomainId = domainId ?? domain?.id;

  const eventTypes = useEventTypes(effectiveAccountId, effectiveDomainId, setLoadError, {
    allowedEventsForTrigger,
    excludeHighFrequency,
  });

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!eventTypes) {
    return <Spinner />;
  }

  // An event that is already configured stays on offer even when the current
  // restrictions would hide it — one saved before the restriction existed, or one the
  // domain has stopped emitting. Otherwise it renders as a bare action name and cannot
  // be picked again once removed.
  const knownValues = new Set(eventTypes.map((eventType) => eventType.value));
  const configuredElsewhere = [actions ?? []]
    .flat()
    .filter((action) => action && !knownValues.has(action))
    .map(
      (action) => standardEventTypes.find((eventType) => eventType.value === action) ?? { label: action, value: action }
    );

  const Component = isMulti ? MultiSelect : Select;

  return (
    <Component
      searchable={true}
      clearable={true}
      data={configuredElsewhere.length ? [...eventTypes, ...configuredElsewhere] : eventTypes}
      placeholder="Filter by event action..."
      onChange={(selectedOptions) => setActions([selectedOptions].flat())}
      renderOption={({ option, checked }) => (
        <Group flex="1" gap="xs">
          <EventIcon eventName={option.value} />
          {option.label}
          {checked && <IconCheck style={{ marginInlineStart: 'auto' }} />}
        </Group>
      )}
      filter={({ options, search }) => {
        if (!search) return options;
        const searchWords = search.toLowerCase().split(' ').filter(Boolean);
        return options.filter((option) => {
          const label = (option.label || '').toLowerCase();
          const value = (option.value || '').toLowerCase();
          return searchWords.every((word) => label.includes(word) || value.includes(word));
        });
      }}
      value={isMulti ? actions : actions[0]}
    />
  );
};

export default EventSelector;
